/*
 * 版权所有 (c) 华为技术有限公司 2024
 * Copyright (c) Huawei Technologies Co., Ltd. 2024. All rights reserved.
 */
import { Injectable } from '@angular/core';
import { type ColumnDef, type SelectOption, I18nService, UNPLACEHOLDER, UtilService } from '@dm/core';
import { healthStatusE } from 'app/common/constants/health-status';
import { MO } from 'app/common/constants/mo';
import { runningStatusE } from 'app/common/constants/running-status';
import { KeyValuePipe } from 'app/common/pipes/key-value.pipe';
import { FeatureConfig } from 'app/config/feature';
import { HardwareStrService } from 'app/feature/system/hardware/hardware-str.service';

import { type NetSpaceMember } from './net-space';
import { NetSpaceTypeE } from './net-space.enum';

@Injectable({
    providedIn: 'root',
})
export class NetSpaceStrService {
    constructor(
        private i18nService: I18nService,
        private utilService: UtilService,
        private keyValuePipe: KeyValuePipe,
        private hardwareStrService: HardwareStrService,
    ) { }

    get = this.i18nService.get;

    i18n = {
        netSpaceLabel: this.get('system.network_netSpace_title'),
        netSpaceName: this.get('common.name_column'),
        netSpaceId: this.get('common.ID_column'),
        netSpaceType: this.get('system.type_column'),
        netSpaceTypeWithColon: this.get('system.type_column', true),
        netSpaceTypeSystem: this.get('system.EnumDefine_FAILOVERGROUP_TYPE_E_SYSTEM'),
        netSpaceTypeCustomized: this.get('system.EnumDefine_FAILOVERGROUP_TYPE_E_CUSTOMIZED'),
        createPageTitle: this.get('system.network_netSpace_create_title'),
        modifyPageTitle: this.get('system.network_netSpace_modify_title'),
        createActionTitle: (name: string): string => this.get('rest.createNetspace_msg', [name]),
        modifyActionTitle: (name: string): string => this.get('rest.modifyNetspace_msg', [name]),
        deleteActionTitle: (name: string): string => this.get('rest.deleteNetspace_msg', [name]),
        disabledTips: this.get('system.network_netSpace_delete_err_tip'),
        deletePromptTitle: this.get('system.network_netSpace_delete_title'),
        deletePromptContent: this.get('system.network_netSpace_deleteCustomNetSpace_confirm_msg'),

        addMemberResult: (member: NetSpaceMember): string => this.get('rest.addMembers_msg', [this.getAddRemoveMemberLabel(member)]),
        removeMemberResult: (member: NetSpaceMember): string => this.get('rest.removeMembers_msg', [this.getAddRemoveMemberLabel(member)]),
        addMemberTitle: this.get('common.addMember_button'),
        removeMemberTitle: this.get('common.removeMember_button'),
        removeMemberPromptContent: this.get('system.network_netSpace_removePort_confirm_msg'),

        // 按钮
        closeBtn: this.get('common.close_button'),
        createBtn: this.get('common.create_button'),
        modifyBtn: this.get('common.modify_button'),
        deleteBtn: this.get('common.delete_button'),
        okBtn: this.get('common.confirm_button'),
        cancelBtn: this.get('common.cancel_button'),
        operationLab: this.get('common.operation_button'),

        leftTitle: this.get('common.available', [this.get('system.network_term_members_tab')]),
        rightTitle: this.get('common.selected', [this.get('system.network_term_members_tab')]),
        location: this.get('common.location_column'),
        healthStatus: this.get('common.healthStatus_column'),
        runningStatus: this.get('common.runningStatus_column'),
        maxUnit: this.get('system.device_controlEnsoure_view_Ethport_maxTransUnit'),
        ethPortHeader: this.get('system.network_term_etherPorts_tab'),
        rocePortHeader: this.get('system.network_term_rocePorts_tab'),
        bondPortHeader: this.get('system.network_term_bondPorts_tab'),
        vlanHeader: this.get('system.hardware_term_VLANs_tab'),
        addMemberPromptContent: this.get('system.network_netSpace_addMemberPort_confirm_msg'),
    };

    /**
     * 获取添加或移除成员命令提示信息
     * @param member netSpace成员
     * @returns 命令提示信息
     */
    getAddRemoveMemberLabel(member: NetSpaceMember): string {
        // eth和roce展示位置信息
        if (member.TYPE === MO.ETH_PORT_TYPE) {
            return `${this.memberTypeLabelMap[member.netSpaceMemberTempType]} ${member.LOCATION}`;
        }

        // 其他展示名称
        return `${this.memberTypeLabelMap[member.netSpaceMemberTempType]} ${member.NAME}`;
    }

    /** NetSpace类型词条 */
    netSpaceTypeStrOpt = (value: string): string => this.utilService.getLabelFromOptions(this.netSpaceTypeOption, value);

    /** NetSpace类型选项 */
    netSpaceTypeOption: SelectOption[] = [
        /** 系统 */
        {
            value: NetSpaceTypeE.SYSTEM,
            label: this.i18n.netSpaceTypeSystem,
        },
        /** 自定义 */
        {
            value: NetSpaceTypeE.CUSTOMIZED,
            label: this.i18n.netSpaceTypeCustomized,
        },
    ];

    /** netSpace类型和rest对象映射 */
    memberTypeRestMo = {
        [MO.ETH_PORT_TYPE]: 'ethPort',
        // 查询roce也用查询eth的接口
        [MO.NVMeoF_RoCE_TYPE]: 'ethPort',
        [MO.BOND_PORT]: 'bondPort',
        [MO.VLAN]: 'vlan',
    };

    /** netSpace类型和名称对象映射 */
    memberTypeLabelMap = {
        [MO.ETH_PORT_TYPE]: this.i18n.ethPortHeader,
        [MO.NVMeoF_RoCE_TYPE]: this.i18n.rocePortHeader,
        [MO.BOND_PORT]: this.i18n.bondPortHeader,
        [MO.VLAN]: this.i18n.vlanHeader,
    };

    /** netSpace成员类型选项 */
    netSpaceMemberTypeOpt: SelectOption[] = [
        {
            label: this.i18n.ethPortHeader,
            value: MO.ETH_PORT_TYPE,
        },
        {
            label: this.i18n.rocePortHeader,
            value: MO.NVMeoF_RoCE_TYPE,
            hide: !FeatureConfig.IsSupportNVMEOverRoCENetWorkAndLif,
        },
        {
            label: this.i18n.bondPortHeader,
            value: MO.BOND_PORT,
            hide: !FeatureConfig.IsSupportCreateBondPort,
        },
        {
            label: this.i18n.vlanHeader,
            value: MO.VLAN,
        },
    ];

    /** 列表配置项集合 */
    columnDefItems: { [key: string]: ColumnDef } = {
        name: {
            field: 'NAME',
            header: this.i18n.netSpaceName,
            filterType: 'search',
            sortable: true,
            columnFilter: true,
        },
        location: {
            field: 'LOCATION',
            header: this.i18n.location,
            filterType: 'search',
            sortable: true,
            columnFilter: true,
            render: (value: string) => value || UNPLACEHOLDER,
        },
        healthStatus: {
            field: 'HEALTHSTATUS',
            header: this.i18n.healthStatus,
            render: (value: string) => this.keyValuePipe.transform(value, healthStatusE),
            columnFilter: true,
            sortable: true,
            filterType: 'dropdown',
        },
        runningStatus: {
            field: 'RUNNINGSTATUS',
            header: this.i18n.runningStatus,
            render: (value: string) => this.keyValuePipe.transform(value, runningStatusE),
            columnFilter: true,
            sortable: true,
            filterType: 'dropdown',
        },
        mtu: {
            field: 'MTU',
            header: this.i18n.maxUnit,
            columnFilter: true,
            sortable: true,
            align: 'right',
            render: (value: string) => value || UNPLACEHOLDER,
        },
        netSpaceMemberType: {
            // 构造的临时类型，用于区分以太网端口和roce端口
            field: 'netSpaceMemberTempType',
            header: this.i18n.netSpaceType,
            sortable: true,
            render: (value: number) => this.netSpaceMemberTypeOpt.find((a) => a.value === value).label || UNPLACEHOLDER,
            options: this.netSpaceMemberTypeOpt,
            filterType: 'dropdown',
            columnFilter: true,
        },
    };

    /** netSpace各类型列表配置 */
    columnDefs: { [key: string]: ColumnDef[] } = {
        /** 以太网端口 */
        eth: [
            {
                ...this.columnDefItems.name,
                hidden: true,
            },
            this.columnDefItems.location,
            {
                ...this.columnDefItems.healthStatus,
                options: this.hardwareStrService.ethPortHealthStatusOptions,
            },
            {
                ...this.columnDefItems.runningStatus,
                options: this.hardwareStrService.ethPortRunningStatusOptions,
            },
        ],
        /** roce端口 */
        roce: [
            {
                ...this.columnDefItems.name,
                hidden: true,
            },
            this.columnDefItems.location,
            {
                ...this.columnDefItems.healthStatus,
                options: this.hardwareStrService.ethPortHealthStatusOptions,
            },
            {
                ...this.columnDefItems.runningStatus,
                options: this.hardwareStrService.ethPortRunningStatusOptions,
            },
        ],
        /** 绑定端口 */
        bond: [
            this.columnDefItems.name,
            {
                ...this.columnDefItems.healthStatus,
                options: this.hardwareStrService.bondPortHealthStatusOptions,
            },
            {
                ...this.columnDefItems.runningStatus,
                options: this.hardwareStrService.bondPortRunningStatusOptions,
            },
        ],
        /** VLAN */
        vlan: [
            this.columnDefItems.name,
            {
                ...this.columnDefItems.runningStatus,
                options: this.hardwareStrService.vlanRunningStatusOptions,
            },
            this.columnDefItems.mtu,
        ],
        /** 已选成员 */
        selectedMember: [
            this.columnDefItems.netSpaceMemberType,
            this.columnDefItems.name,
            // 以太网端口名称容易重复，展示位置信息，绑定端口和VLAN自适应为--
            this.columnDefItems.location,
            {
                ...this.columnDefItems.runningStatus,
                hidden: true,
            },
        ],
    };
}
